import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, Loader2, Plus, Trash2, X } from 'lucide-react';
import { chatbotApi, type ChatConversationSummary } from '@/lib/api';
import { formatRelative } from './chatTime';

export function ChatHistorySidebar({
  conversations,
  activeId,
  isLoading,
  mobileView,
  usage,
  onNew,
  onSelect,
  onDeleted,
}: {
  conversations: ChatConversationSummary[];
  activeId: string | null;
  isLoading: boolean;
  mobileView: 'history' | 'chat';
  usage: import('@/lib/api').ChatbotLimitsResponse | undefined;
  onNew: () => void;
  onSelect: (id: string) => void;
  onDeleted: (id: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      className={`border-border bg-subtle/40 flex min-h-0 w-full flex-1 flex-col border-r md:w-[220px] md:flex-none ${
        mobileView === 'history' ? 'flex' : 'hidden md:flex'
      }`}
    >
      <div className="border-border flex items-center justify-between border-b px-3 py-3">
        <span className="text-text-tertiary text-[10px] font-bold tracking-widest uppercase">
          {t('chatbot.history')}
        </span>
        <button
          type="button"
          onClick={onNew}
          className="text-text-secondary hover:text-accent rounded-md p-1.5 transition-colors"
          title={t('chatbot.newConversation')}
          aria-label={t('chatbot.newConversation')}
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="text-text-tertiary h-4 w-4 animate-spin" />
          </div>
        ) : conversations.length === 0 ? (
          <div className="text-text-tertiary px-3 py-6 text-xs">{t('chatbot.noConversations')}</div>
        ) : (
          <AnimatePresence initial={false}>
            {conversations.map((conv) => (
              <ChatConversationItem
                key={conv.id}
                conversation={conv}
                isActive={activeId === conv.id}
                onSelect={() => onSelect(conv.id)}
                onDeleted={() => onDeleted(conv.id)}
              />
            ))}
          </AnimatePresence>
        )}
      </div>
      {usage && (
        <div className="border-border text-text-tertiary border-t px-3 py-2 text-[10px]">
          {usage.messagesLimitThisMonth < 0
            ? t('chatbot.usageUnlimited')
            : t('chatbot.usageCounter', {
                used: usage.messagesUsedThisMonth,
                limit: usage.messagesLimitThisMonth,
              })}
        </div>
      )}
    </div>
  );
}

function ChatConversationItem({
  conversation,
  isActive,
  onSelect,
  onDeleted,
}: {
  conversation: ChatConversationSummary;
  isActive: boolean;
  onSelect: () => void;
  onDeleted: () => void;
}) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const remove = useMutation({
    mutationFn: () => chatbotApi.deleteConversation(conversation.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat-conversations'] });
      onDeleted();
    },
  });

  const relative = useMemo(
    () => formatRelative(conversation.updatedAt, i18n.language),
    [conversation.updatedAt, i18n.language],
  );

  return (
    <motion.button
      type="button"
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20, height: 0 }}
      transition={{ duration: 0.18 }}
      onClick={() => {
        if (confirming) return;
        onSelect();
      }}
      className={`group block w-full border-l-2 px-3 py-2.5 text-left text-xs transition-colors ${
        isActive
          ? 'bg-accent/10 border-accent text-text-primary'
          : 'text-text-secondary hover:bg-subtle border-transparent'
      }`}
    >
      {confirming ? (
        <div className="flex items-center justify-between gap-2 text-red-500">
          <span className="text-[11px] font-semibold">{t('chatbot.deleteConfirm')}</span>
          <div className="flex items-center gap-1">
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                remove.mutate();
              }}
              className="rounded p-1 hover:bg-red-500/20"
              aria-label={t('chatbot.delete')}
            >
              {remove.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Check className="h-3 w-3" />
              )}
            </span>
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                setConfirming(false);
              }}
              className="rounded p-1 hover:bg-red-500/20"
              aria-label={t('chatbot.cancel')}
            >
              <X className="h-3 w-3" />
            </span>
          </div>
        </div>
      ) : (
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="line-clamp-2 leading-tight font-medium">
              {conversation.title ?? t('chatbot.newConversation')}
            </p>
            <p className="text-text-tertiary mt-1 text-[10px]">{relative}</p>
          </div>
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              setConfirming(true);
            }}
            className="text-text-tertiary rounded p-1 opacity-0 transition-opacity group-hover:opacity-100 hover:text-red-500"
            aria-label={t('chatbot.delete')}
          >
            <Trash2 className="h-3 w-3" />
          </span>
        </div>
      )}
    </motion.button>
  );
}
